import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, StatusChip } from '@smena/ui';
import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import { fetchSystem } from '@/features/system/api';
import { TelegramProxyCard } from '@/features/telegram/TelegramProxyCard';
import { IgnoredChatsCard } from '@/features/ignored-chats/IgnoredChatsCard';

export function SystemPage() {
  const q = useQuery({ queryKey: ['system'], queryFn: fetchSystem, refetchInterval: 15_000 });

  return (
    <>
      <PageHeader
        title="Система"
        subtitle="Что за сборка сейчас крутится, жива ли БД и на какой LLM мы сидим. Обновляется каждые 15 c."
      />
      {/* Карточка Telegram живёт выше диагностики и вне её QueryState: это единственная на
          странице настройка, которую правят, а не читают, и падение /api/admin/system не
          должно её прятать. */}
      <div className="mb-4 flex flex-col gap-4">
        <TelegramProxyCard />
        <IgnoredChatsCard />
      </div>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data ? (
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Сборка</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 text-sm">
                <Row label="Версия" value={q.data.version} />
                <Row label="Node" value={q.data.nodeVersion} />
                <Row label="Аптайм" value={formatUptime(q.data.uptimeSec)} />
                <Row label="Старт" value={new Date(q.data.startedAt).toLocaleString('ru-RU')} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>База данных</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted">Состояние</span>
                  <StatusChip tone={q.data.db.ok ? 'green' : 'red'}>
                    {q.data.db.ok ? 'ok' : 'недоступна'}
                  </StatusChip>
                </div>
                <Row
                  label="Пинг"
                  value={q.data.db.latencyMs !== null ? `${q.data.db.latencyMs} мс` : '-'}
                />
                <Row label="Миграций применено" value={String(q.data.migrations.applied)} />
                {q.data.db.error ? (
                  <p className="text-xs text-destructive">{q.data.db.error}</p>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>LLM</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 text-sm">
                <Row label="Модель" value={q.data.llm.model ?? 'не задана (L0)'} />
                <Row label="Хост" value={q.data.llm.baseUrlHost ?? '-'} mono />
                <Row label="Ключ" value={q.data.llm.keyMasked ?? '-'} mono />
                <div className="my-1 border-t border-line" />
                <Row label="Embeddings" value={q.data.embed.model ?? 'stub'} />
                <Row label="Хост embeddings" value={q.data.embed.baseUrlHost ?? '-'} mono />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Env-флаги</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-2 text-sm">
                {Object.entries(q.data.env).map(([k, v]) => (
                  <Row key={k} label={k} value={v} mono />
                ))}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </QueryState>
    </>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-muted">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}

function formatUptime(sec: number): string {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d) return `${d} д ${h} ч`;
  if (h) return `${h} ч ${m} мин`;
  return `${m} мин`;
}
